import React, { useState, useEffect } from 'react';

export default function Calendar({ value, onChange, settings, className = '' }) {
	const initialDate = value ? new Date(value) : new Date();

	initialDate.setDate(1); // Set to first day of month for navigation

	const [currentDate, setCurrentDate] = useState(initialDate);

	// Update displayed month when value changes
	useEffect(() => {
		if (value) {
			const valueDate = new Date(value);
			const currentMonth = currentDate.getMonth();
			const currentYear = currentDate.getFullYear();
			const valueMonth = valueDate.getMonth();
			const valueYear = valueDate.getFullYear();

			// Only update if the month/year changed
			if (currentMonth !== valueMonth || currentYear !== valueYear) {
				setCurrentDate(new Date(valueYear, valueMonth, 1));
			}
		}
	}, [value]);

	// Static calendar configuration
	const monthNames = settings?.monthNames?.split(',') || [
		'Ianuarie',
		'Februarie',
		'Martie',
		'Aprilie',
		'Mai',
		'Iunie',
		'Iulie',
		'August',
		'Septembrie',
		'Octombrie',
		'Noiembrie',
		'Decembrie',
	];
	const dayNames = settings?.dayNames?.split(',').map((day) => day.slice(0, 3)) || [
		'Mo',
		'Tu',
		'We',
		'Th',
		'Fr',
		'Sa',
		'Su',
	];

	// Parse disabled dates from settings (format: "1.11.2025, 12.11.2025, 13.11.2025")
	const parseDisabledDates = (disabledDatesString) => {
		if (!disabledDatesString) return [];

		return disabledDatesString?.split(',').map((dateStr) => {
			const [day, month, year] = dateStr.trim()?.split('.');

			return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
		});
	};

	const disabledDates = parseDisabledDates(settings?.disabledDays);
	// Calculate min and max dates
	const minDate = new Date();
	const maxDate = new Date();

	maxDate.setDate(maxDate.getDate() + parseInt(settings?.maxDays || 30));

	const getDaysInMonth = (date) => {
		const year = date.getFullYear();
		const month = date.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const daysInMonth = lastDay.getDate();
		const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0

		const days = [];

		// Add empty cells for days before the first day of the month
		for (let i = 0; i < startingDayOfWeek; i++) {
			days.push(null);
		}

		// Add all days of the month
		for (let day = 1; day <= daysInMonth; day++) {
			days.push(new Date(year, month, day));
		}

		return days;
	};

	const isDateSelectable = (date) => {
		if (!date) return false;

		const today = new Date();

		today.setHours(0, 0, 0, 0);

		// Check if date is before minDate
		if (minDate && date < minDate) return false;

		// Check if date is after maxDate
		if (maxDate && date > maxDate) return false;

		// Check if date is in disabled dates
		const isDisabled = disabledDates.some((disabledDate) => {
			return date.toDateString() === disabledDate.toDateString();
		});

		if (isDisabled) return false;

		return date >= today;
	};

	const isDateSelected = (date) => {
		if (!date || !value) return false;

		return date.toDateString() === value.toDateString();
	};

	const handleDateClick = (date) => {
		if (isDateSelectable(date) && onChange) {
			onChange(date);
		}
	};

	const goToPreviousMonth = () => {
		setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
	};

	const goToNextMonth = () => {
		setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
	};

	const days = getDaysInMonth(currentDate);

	return (
		<div className={`simple-calendar ${className}`}>
			<div className="simple-calendar__header">
				<h3 className="simple-calendar__title">
					{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
				</h3>
				<div className="simple-calendar__navigation">
					<button className="simple-calendar__nav-button" onClick={goToPreviousMonth} type="button">
						‹
					</button>
					<button className="simple-calendar__nav-button" onClick={goToNextMonth} type="button">
						›
					</button>
				</div>
			</div>

			<div className="simple-calendar__days-header">
				{dayNames.map((day) => (
					<div key={day} className="simple-calendar__day-name">
						{day}
					</div>
				))}
			</div>

			<div className="simple-calendar__days-grid">
				{days.map((date, index) => (
					<div
						key={index}
						className={`simple-calendar__day ${!date ? 'simple-calendar__day--empty' : ''} ${
							date && !isDateSelectable(date) ? 'simple-calendar__day--disabled' : ''
						} ${date && isDateSelectable(date) ? 'simple-calendar__day--selectable' : ''} ${
							date && isDateSelected(date) ? 'simple-calendar__day--selected' : ''
						}`}
						onClick={() => date && handleDateClick(date)}
					>
						{date && date.getDate()}
					</div>
				))}
			</div>
		</div>
	);
}
